import {
  Box,
  MenuItem,
  Modal,
  styled,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import React from "react";
import * as Yup from "yup";
import api from "../../api/axiosInstance";
import { Button, FlexRow, Spacer } from "../../components";
import { FREQUENCIES, USER_ROLES } from "../../data";
import { ColorsCodeType, UserProps, UserRoles, UserRolesEnum } from "../../typings";
import { useSnackbar } from "../../context/SnackbarContext";

const StyledModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled(Box)`
  background-color: ${({ theme }) => theme.palette.background.paper};
  border-radius: 8px;
  padding: 24px;
  width: 100%;
  max-width: 550px;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  role: Yup.string()
    .oneOf(
      USER_ROLES.map((role) => role.value),
      "Invalid role"
    )
    .required("Role is required"),
  donor_contribution_reminder_frequency: Yup.string().when("role", {
    is: UserRolesEnum.DONOR,
    then: (schema) =>
      schema.required("Contribution reminder frequency is required"),
    otherwise: (schema) => schema.optional(),
  }),
});

interface CreateUserModalProps {
  open: boolean;
  onClose: () => void;
  user: UserProps;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({
  open,
  onClose,
  user,
}) => {
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();

  const handleSubmit = async (
    values: UserProps,
    {
      setSubmitting,
      setErrors,
    }: {
      setSubmitting: (isSubmitting: boolean) => void;
      setErrors: (errors: any) => void;
    }
  ) => {
    try {
      // Build payload — only include donor field when role is Donor
      const payload: Partial<UserProps> = {
        name: values.name,
        email: values.email,
        password: values.password,
        role: values.role,
      };

      if (values.role === UserRolesEnum.DONOR) {
        payload.donor_contribution_reminder_frequency =
          values.donor_contribution_reminder_frequency;
      }

      if (!user?.id) {
        await api.post("/users/", JSON.stringify(payload), {
          headers: { "Content-Type": "application/json" },
        });
        showSnackbar("User created successfully!", "success");
      } else {
        await api.patch(
          `/users/${user.id.toString()}/`,
          JSON.stringify(payload),
          {
            headers: { "Content-Type": "application/json" },
          }
        );
        showSnackbar("User updated successfully!", "success");
      }

      onClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.messages?.[0] ||
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        `Failed to ${user?.id ? "update" : "create"} user. Please try again.`;

      showSnackbar(errorMessage, "error");

      if (error?.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StyledModal open={open} onClose={onClose}>
      <ModalContent>
        <Typography variant="h6" sx={{ fontWeight: "600" }}>
          {!user?.id ? "Create User" : "Update User"}
        </Typography>
        <Spacer vertical={2} />
        <Formik
          initialValues={user}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ errors, touched, isSubmitting, values, setFieldValue }) => (
            <StyledForm>
              <Field
                as={TextField}
                name="name"
                label="Name"
                fullWidth
                error={touched.name && errors.name}
                helperText={touched.name && errors.name}
              />
              <Field
                as={TextField}
                name="email"
                label="Email"
                fullWidth
                error={touched.email && errors.email}
                helperText={touched.email && errors.email}
              />
              <Field
                as={TextField}
                select
                name="role"
                label="Role"
                fullWidth
                error={touched.role && errors.role}
                helperText={touched.role && errors.role}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const selectedRole = e.target.value as UserRoles;
                  setFieldValue("role", selectedRole);
                  // Clear frequency when not donor
                  if (selectedRole !== UserRolesEnum.DONOR) {
                    setFieldValue(
                      "donor_contribution_reminder_frequency",
                      undefined
                    );
                  }
                }}
              >
                {USER_ROLES.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Field>

              {/* ── Donor-only: Contribution Reminder Frequency ──────────── */}
              {values.role === UserRolesEnum.DONOR && (
                <TextField
                  select
                  name="donor_contribution_reminder_frequency"
                  label="Contribution Reminder Frequency"
                  fullWidth
                  value={
                    values.donor_contribution_reminder_frequency ?? ""
                  }
                  onChange={(e) =>
                    setFieldValue(
                      "donor_contribution_reminder_frequency",
                      e.target.value
                    )
                  }
                  error={
                    touched.donor_contribution_reminder_frequency &&
                    Boolean(errors.donor_contribution_reminder_frequency)
                  }
                  helperText={
                    touched.donor_contribution_reminder_frequency &&
                    errors.donor_contribution_reminder_frequency
                  }
                >
                  {FREQUENCIES.map((freq) => (
                    <MenuItem key={freq.id} value={freq.id}>
                      {freq.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <FlexRow justify="flex-end" align="center">
                <Button
                  buttonColor={
                    theme.palette.colors["black"]["600"] as ColorsCodeType
                  }
                  textColor={
                    theme.palette.colors["white"]["400"] as ColorsCodeType
                  }
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Spacer horizontal={1} />

                <Button type="submit" disabled={isSubmitting}>
                  {user?.id ? "Update User" : "Create"}
                </Button>
              </FlexRow>
            </StyledForm>
          )}
        </Formik>
      </ModalContent>
    </StyledModal>
  );
};

export default CreateUserModal;
